<template>
  <div
    class="modal has-overflow"
    :class="{ 'is-active': value }">

    <div class="modal-background"
      @click="close()">
    </div>
    
    <div class="modal-content">

      <div class="modal-card">

        <header class="modal-card-head">
          <p class="modal-card-title">
            Add items to delivery
          </p>
          <button
            class="delete"
            aria-label="close"
            @click="close()">
          </button>
        </header>

        <div class="modal-card-body">

          <article 
            v-if="!loading && filteredInventory.length == 0" 
            class="message is-info is-fullwidth">
            <div class="message-body">
              There are no additional items available from this 
              project that can be added to this delivery.
            </div>
          </article>

          <table 
            class="table is-fullwidth has-small-font sticky-header" 
            v-if="!loading && filteredInventory.length > 0">

            <thead>
              <tr>
                <th 
                  v-for="(header, headerIndex) in headers"
                  :key="headerIndex">
                  {{ header }}
                </th>
              </tr>
            </thead>

            <tbody>
              <template 
                v-for="inventoryItem in projectInventory">
                <tr
                  class="v-centered subheader"
                  :key="inventoryItem.id">

                  <td>
                    {{ inventoryItem.inventory.product_master.product_category.category }}
                  </td>         
                  <td>
                    <span v-if="inventoryItem.inventory.product_master.product_subcategory">
                      {{ inventoryItem.inventory.product_master.product_subcategory.subcategory }}
                    </span>
                    <span v-else class="has-text-grey">
                      No subcategory
                    </span>
                  </td>  
                  <td>
                    {{ inventoryItem.inventory.product_master.name }}
                  </td>
                  <td class="text--nowrap is-size-7">
                    <template v-if="inventoryItem.inventory.product_master.product_category.has_dimensions">
                      {{ lwt(inventoryItem.inventory.stocking_unit) }}
                    </template>
                    <span>
                      {{ inventoryItem.inventory.product_master.size }}
                    </span>
                  </td>
                  <td>
                    {{ inventoryItem.qty_needed }}
                  </td>
                  <td>
                    {{ inventoryItem.qty_available }}
                  </td>

                </tr>

                <template
                  v-if="inventoryItem.inventory.stocking_unit && 
                    inventoryItem.inventory.stocking_unit.storage_unit.length > 0">
                  <tr 
                    :key="inventoryItem.id + '-warehouse-locations-header'"
                    class="v-centered is-narrow has-background-light has-tiny-font">
                    <th></th>
                    <th>Warehouse</th>
                    <th>Location</th>
                    <th>Storage</th>
                    <th>Storage Type</th>
                    <th>Quantity Stored</th>
                  </tr>
                  <tr 
                    class="has-background-light v-centered"
                    v-for="storageUnit in inventoryItem.inventory.stocking_unit.storage_unit"
                    :key="storageUnit.id">
                    <td>
                      <v-checkbox
                        :value="deliveryStorageIds.includes(storageUnit.storage_id)"
                        @input="toggleStorageId(storageUnit.storage_id)">
                      </v-checkbox>
                    </td>
                    <td>
                      <span v-if="storageUnit.storage.warehouse_location">
                        ({{ storageUnit.storage.warehouse_location.warehouse.warehouse_code }}) 
                        {{ storageUnit.storage.warehouse_location.warehouse.warehouse_name }}
                      </span>
                      <span v-else class="has-text-grey-light">
                        No warehouse information
                      </span>
                    </td>
                    <td>
                      <span v-if="storageUnit.storage.warehouse_location">
                        ({{ storageUnit.storage.warehouse_location.location_code }}) 
                        {{ storageUnit.storage.warehouse_location.location_name }}
                      </span>
                      <span v-else class="has-text-grey-light">
                        No warehouse space information
                      </span>
                    </td>
                    <td>
                      ({{ storageUnit.storage.storage_code }})
                      {{ storageUnit.storage.storage_name }}
                    </td>
                    <td>
                      {{ storageUnit.storage.storage_type.storage_type_name }}
                    </td>
                    <td>
                      {{ storageUnit.qty }}
                    </td>
                  </tr>
                </template>

              </template>
            </tbody>
          </table>

        </div>

        
        <footer class="modal-card-foot">
          <button 
            :disabled="!valid"
            class="button is-primary" 
            @click.stop="save()">
            Save
          </button>
          <button 
            class="button" 
            @click.stop="close(true)">
            Cancel
          </button>
        </footer>

      </div>
    </div>
  </div>
</template>

<script>
import VCheckbox from '@/components/base/VCheckbox'

import { mapState, mapActions, mapMutations } from 'vuex'

export default {
  props: {
    value: {
      type: Boolean
    },
    projectId: {
      type: [String, Number]
    },
    orderId: {
      type: [String, Number]
    },
    exclude: {
      type: Array
    }
  },
  components: { 
    VCheckbox 
  },
  data() {
    return {
      valid: false,
      headers: [
        'Product Category', 'Product Subcategory', 'Product', 
        'Product Size', 'Quantity Needed', 'Quantity Available'
      ],
      deliveryStorageIds: []
    }
  },
  computed: {
    ...mapState("inventoryProject", ["projectInventory", "loading"]),
    filteredInventory() {
      return this.projectInventory.map(d => {
        let res = JSON.parse(JSON.stringify(d));
        res.inventory.stocking_unit.storage_unit = res.inventory.stocking_unit ? 
          res.inventory.stocking_unit.storage_unit.filter(
            d => !this.exclude.includes(d.storage_id)
          ) : []
        return res
      }).filter(d => d.inventory.stocking_unit.storage_unit.length > 0);
    },
    deliveryStorages() {
      return this.projectInventory.map(d => {
        let res = JSON.parse(JSON.stringify(d));
        res.inventory.stocking_unit.storage_unit = res.inventory.stocking_unit ? 
          res.inventory.stocking_unit.storage_unit.filter(
            d => this.deliveryStorageIds.includes(d.storage_id)
          ) : []
        return res
      }).filter(d => d.inventory.stocking_unit.storage_unit.length > 0);
    }
  },
  watch: {
    projectId(id) {
      this.getInventory({ projectId: id });
    }
  },
  created() {
    this.initialize();
  },
  methods: {
    ...mapActions("inventoryProject", ["getInventory"]),
    ...mapActions("delivery", ["addItem"]),
    ...mapMutations("app", ["setNotification"]),
    initialize() {
      this.getInventory({ project_id: this.projectId });
    },
    lwt(product) {
      if (product) {
        return `L${product.length_in_str}" x W${product.width_in_str}" x T${product.thick_in_str}"`
      }
    },
    toggleStorageId(id) {
      let index = this.deliveryStorageIds.indexOf(id);
      if (index == -1) {
        this.deliveryStorageIds.push(id);
      } else {
        this.deliveryStorageIds.splice(index, 1);
      }
    },
    save() {
      let items = this.deliveryStorages.map(d => 
        d.inventory.stocking_unit.storage_unit
      ).flat(1).map(d => {
        return {
          delivery_id: this.deliveryId,
          storage_id: d.storage_id
        }
      });

      let promises = [];

      items.forEach(item => {
        promises.push(this.addItem(item));
      });

      Promise.all(promises).then(() => {
        this.setNotification({
          status: true,
          type: 'success',
          message: 'Delivery items successfully added'
        });

        this.close(true);
      });
    },
    close(clear=false) {
      this.$emit('input', false);
      if (clear) {
        this.clearData();
      }
    },
    clearData() {
      Object.assign(this.$data, this.$options.data.call(this));
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/styles/modal.scss';
</style>